// src/utils/markdown.ts
//
// 轻量级 Markdown 清理工具：移除常见 Markdown 语法标记，保留纯文本内容。
// 处理顺序由外到内：先移除外围标记（链接、图片），再移除内层标记（加粗、斜体）。

/**
 * 移除文本中的 Markdown 语法标记，返回纯文本。
 * 支持的语法：
 *   - 标题标记：`# `、`## ` 等
 *   - 加粗标记：`**text**`、`__text__`
 *   - 斜体标记：`*text*`、`_text_`
 *   - 行内代码：`` `code` ``
 *   - 链接：`[text](url)` → `text`
 *   - 图片：`![alt](url)` → 移除整行
 *   - 有序列表：`1. text` → `text`
 *   - 无序列表：`- text`、`* text` → `text`
 *   - 引用：`> text` → `text`
 *   - 水平线：`---`、`***` → 移除
 *   - 多余空行压缩
 */
export function stripMarkdown(text: string): string {
    if (!text) return "";

    let result = text;

    // 1. 移除图片 Markdown（整行）
    result = result.replace(/!\[[^\]]*\]\([^)]*\)/g, "");

    // 2. 移除链接 Markdown，保留链接文字
    result = result.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1");

    // 3. 移除行内代码反引号
    result = result.replace(/`([^`]+)`/g, "$1");

    // 4. 移除加粗标记 **text** / __text__
    result = result.replace(/\*\*([^*]+)\*\*/g, "$1");
    result = result.replace(/__([^_]+)__/g, "$1");

    // 5. 移除斜体标记 *text* / _text_（单字符包裹，谨慎处理）
    result = result.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1$2");
    result = result.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, "$1$2");

    // 6. 移除标题行首的 # 标记
    result = result.replace(/^\s{0,3}#{1,6}\s+/gm, "");

    // 7. 保留有序列表序号（1. 2. 3. 等），仅清理 Markdown 加粗/斜体等内联标记

    // 8. 移除无序列表标记（- * + 开头）
    result = result.replace(/^\s{0,3}[-*+]\s+/gm, "");

    // 9. 移除引用标记（> 开头）
    result = result.replace(/^\s{0,3}>\s+/gm, "");

    // 10. 移除水平线
    result = result.replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/gm, "");

    // 11. 压缩多余空行（3+ 连续空行 → 2 个空行）
    result = result.replace(/\n{3,}/g, "\n\n");

    // 12. 移除首尾空白
    result = result.trim();

    return result;
}
